//Library Imports
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
//Local Imports
import { RootState } from "store";
import { clearCopyNode, updatedWorkflowAPI } from "../actions";
import RecursiveComponent from "./RecursiveComponent";
import TriggerWorkflow from "./TriggerWorkflow";
import { ConditionEnum } from "../interfaces";
import { EVENT_IDS } from "../constants";

const DropAreaItems = () => {
  // Redux States
  const workflow = useSelector((state: RootState) => state.automation.workflow.flow);
  const workflowDetail = useSelector((state: RootState) => state.automation.workflow.detail);
  const triggerId = useSelector((state: RootState) => state.automation.workflow.detail.triggerId);
  const triggerEmailSent = useSelector((state: RootState) => state.automation.workflow.detail.triggerEmailSent);
  const newlyAddedNodes: string[] = useSelector((state: RootState) => state.automation.workflow?.detail?.newlyAddedNodes);
  const copiedNode = useSelector((state: RootState) => state.automation.workflow.copiedNode);
  const [firstRender, setFirstRender] = useState(false);
  const dispatch = useDispatch();

  const ref = useRef(null);
  useEffect(() => {
    const divFour = document.getElementById("workflow-container");
    divFour && divFour.scrollTo((ref?.current.offsetWidth - divFour.offsetWidth) / 2, 0);
  }, []);

  const checkKeyPress = useCallback(
    (e) => {
      const { keyCode } = e;
      // esc key code
      if (keyCode === 27 && copiedNode.hasOwnProperty("nodeId")) {
        dispatch(clearCopyNode());
      }
    },
    [copiedNode]
  );

  useEffect(() => {
    window.addEventListener("keydown", checkKeyPress);
    return () => {
      window.removeEventListener("keydown", checkKeyPress);
    };
  }, [checkKeyPress]);

  // added the following useEffect for autosave feature
  useEffect(() => {
    // workflow id should not be zero.
    if (firstRender) {
      let workflowId = workflowDetail?.id;
      if (workflowId) {
        updatedWorkflowAPI(
          {
            workflowId,
            workflow,
            newlyAddedNodes,
          },
          true
        );
      }
    } else {
      setFirstRender(true)
    }
  }, [workflow]);

  const [result, setResult] = useState(1);
  const number = Number(result);

  function setZoomIn() {
    if (result < 1) {
      setResult(number + Number(0.1));
    }
  }

  function setZoomOut() {
    if (result > 0.3) {
      setResult(number - Number(0.1));
    }
  }

  function checkTriggerName(triggerName: String) {
    if (triggerName === ConditionEnum.HAS_OPENED) return `Contact ${triggerName} an email:`
    else if (triggerName === ConditionEnum.HAS_CLICKED) return `Contact ${triggerName} on any link in the email:`
    else if (triggerName === ConditionEnum.SEQUENCE) return `Contact receives an email:`
    return "Contact an email:"
  }

  let workflowKeys = Object.keys(workflow);
  return (
    <Wrapper className="workflow-container" id="workflow-container">
      <div className="workflow-container-inner" style={{ transform: `scale(${result})` }} ref={ref}>
        <div className="info-workflow">Start this automation when one of the following actions take place</div>
        <TriggerWorkflow
          triggerType={
            triggerId === EVENT_IDS.SHARE_PROFILE ? "Share Profile" : checkTriggerName(workflowDetail?.triggerName)
          }
          actionRefrence={`${workflowDetail?.emailName || ""}`}
          emailId={workflowDetail?.emailId}
          campaignId={workflowDetail?.campaignId}
          versionEmailType={workflowDetail?.versionEmailType}
          downline={true}
          disabled={false}
          hasChildren={workflowKeys.length > 0}
          scaling={result <= 0.6 ? "scaled" : "no-scaled"}
          triggerEmailSent={triggerEmailSent}
          controlName={workflow[workflowKeys[0]]?.detail.controlName}
        />
        {workflowKeys.length ? (
          <RecursiveComponent
            id={workflowKeys[0]}
            item={workflow[workflowKeys[0]]}
            key={workflowKeys[0]}
            scaling={result <= 0.6 ? "scaled" : "no-scaled"}
          />
        ) : null}
      </div>
      <div className="zoom-container">
        <div className="zoom-plus zoom-btn" onClick={setZoomIn}>
          +
        </div>
        <div className="zoom-minus zoom-btn" onClick={setZoomOut}>
          _
        </div>
      </div>
    </Wrapper>
  );
};

export default DropAreaItems;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: calc(100vw - 530px);
  max-height: calc(100vh - 150px);
  display: block;
  padding-bottom: 40px;
  position: relative;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #001760 transparent;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    max-height: calc(100vh - 110px);
    max-width: calc(100vw - 430px);
    //Safari (7.1+)
    _::-webkit-full-page-media,
    _:future,
    :root .safari_only {
      min-height: calc(100vh - 100px);
    }
    //Safari (from 6.1 to 10)
    @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
      @media {
        min-height: calc(100vh - 100px);
      }
    }
    //Safari (10.1+)
    @media not all and (min-resolution: 0.001dpcm) {
      @media {
        min-height: calc(100vh - 100px);
      }
    }
  }
  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    border-radius: 5px;
  }
  /* Track */
  &::-webkit-scrollbar-track {
    background: #fff;
    margin-top: 10px;
    margin-bottom: 15px;
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #001760;
  }
  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  .workflow-popover {
    z-index: 1;
    &.workflow-popover-right {
      .ant-popover-content {
        margin-right: -40px;
      }
      .ant-popover-arrow {
        left: 12px;
      }
    }
    .ant-popover-arrow {
      border-width: 10px;
      bottom: 4px;
      left: 5px;
      right: auto;
    }
    .ant-popover-inner {
      border-radius: 6px;
      box-shadow: 0 0 40px rgba(0, 0, 0, 0.19);
      .ant-popover-inner-content {
        padding: 10px 16px;
        color: #5e7079;
        font-size: 16px;
        font-weight: 400;
        font-family: "Roboto", sans-serif;
        min-width: 50px;
        white-space: nowrap;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 12px;
        }
        ul {
          margin: 0;
          padding: 0;
          list-style: none;
          li {
            padding: 0 2px;
            margin: 0 2px;
            cursor: pointer;
            display: inline;
          }
        }
      }
    }
  }
  .workflow-container-inner {
    transform: scale(1);
    transform-origin: 50% 0%;
    display: table;
    transition-duration: 0.5s;
    margin: 0 auto;
  }
  .zoom-container {
    position: fixed;
    bottom: 20px;
    left: 130px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      left: 80px;
    }
    .zoom-btn {
      cursor: pointer;
      background-color: #27ce0a;
      color: #fff;
      padding: 2px 8px;
      text-align: center;
      font-size: 28px;
      line-height: 24px;
      overflow: hidden;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 1px 5px;
        font-size: 24px;
        line-height: 22px;
      }
      &.zoom-plus {
        border-radius: 6px 6px 0 0;
        margin-bottom: 2px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          border-radius: 4px 4px 0 0;
        }
      }
      &.zoom-minus {
        border-radius: 0 0 6px 6px;
        line-height: 0px;
        min-height: 28px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          min-height: 24px;
          border-radius: 0 0 4px 4px;
        }
      }
    }
  }
  .info-workflow {
    width: 100%;
    text-align: center;
    margin-bottom: 32px;
    margin-top: 16px;
    font-size: 16px;
    font-weight: 400;
    color: #7e7e7e;
    font-family: "Roboto", sans-serif;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-bottom: 15px;
      margin-top: 10px;
      font-size: 12px;
    }
  }
  .drop-area {
    height: 600px;
  }
  .active-drop {
    background-color: green;
  }
`;
